import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {AnimationTrimControlsProps} from './animation-trim-controls.types';
import styles from './animation-trim-controls.module.scss';
import {AnimationTrimThumbnailStrip} from '@Components/ai-chat-window/components/animation-trim-thumbnail-strip/animation-trim-thumbnail-strip';
import {AnimationTrimRangeSlider} from '@Components/ai-chat-window/components/animation-trim-range-slider/animation-trim-range-slider';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

const MIN_TRIM_GAP = 0.5;

export function AnimationTrimControls({
  source,
  duration,
  startTime,
  endTime,
  videoWidth,
  videoHeight,
  onConfirm,
  onCancel: _onCancel,
  onPlay,
  onPause,
  onSeek,
  videoEl,
  preRenderedCanvas,
}: AnimationTrimControlsProps): ReactElement {
  const [localStartTime, setLocalStartTime] = useState(startTime);
  const [localEndTime, setLocalEndTime] = useState(endTime);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(startTime);
  const [dragPlayheadPercent, setDragPlayheadPercent] = useState<number | null>(null);
  const [isStripReady, setIsStripReady] = useState(Boolean(preRenderedCanvas));
  const stripContainerRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const dragPlayheadPercentRef = useRef<number | null>(null);
  const isDraggingPlayheadRef = useRef(false);
  dragPlayheadPercentRef.current = dragPlayheadPercent;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === ' ') {
        e.preventDefault();
        onConfirm(localStartTime, localEndTime);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return (): void => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [localStartTime, localEndTime, onConfirm]);

  useEffect(() => {
    const el = videoEl;
    if (!el) {
      return;
    }

    const onTimeUpdate = (): void => {
      setCurrentTime(el.currentTime);
    };
    const onPlay = (): void => {
      setIsPlaying(true);
    };
    const onPause = (): void => {
      setIsPlaying(false);
    };
    const onEnded = (): void => {
      setIsPlaying(false);
    };

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);

    return (): void => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [videoEl]);

  useEffect(() => {
    const el = videoEl;
    if (!el || duration <= 0) {
      return;
    }
    let rafId: number;
    const update = (): void => {
      if (playheadRef.current) {
        const drag = dragPlayheadPercentRef.current;
        const pct = drag !== null ? drag : (el.currentTime / duration) * 100;
        playheadRef.current.style.left = `${pct}%`;
      }
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return (): void => {
      cancelAnimationFrame(rafId);
    };
  }, [videoEl, duration]);

  const handlePlayPause = (): void => {
    const el = videoEl;
    if (!el) {
      return;
    }
    if (isPlaying) {
      onPause();
    } else {
      if (el.currentTime >= el.duration) {
        onSeek(0);
      }
      onPlay();
    }
  };

  const getRatioFromClientX = (clientX: number): number => {
    const container = stripContainerRef.current;
    if (!container) {
      return 0;
    }
    const rect = container.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const handleStripMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    isDraggingPlayheadRef.current = true;
    const ratio = getRatioFromClientX(e.clientX);
    setDragPlayheadPercent(ratio * 100);

    const onMouseMove = (moveEvent: MouseEvent): void => {
      if (isDraggingPlayheadRef.current) {
        setDragPlayheadPercent(getRatioFromClientX(moveEvent.clientX) * 100);
      }
    };
    const onMouseUp = (upEvent: MouseEvent): void => {
      isDraggingPlayheadRef.current = false;
      setDragPlayheadPercent(null);
      if (duration > 0) {
        onSeek(getRatioFromClientX(upEvent.clientX) * duration);
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleRangeChange = (values: [number, number]): void => {
    setLocalStartTime(values[0]);
    setLocalEndTime(values[1]);
  };

  const formatTime = (seconds: number): string => {
    const total = Math.round(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const selectedDuration = Math.max(0, Math.round(localEndTime - localStartTime));
  const badgeRightPercent = duration > 0 ? ((duration - localEndTime) / duration) * 100 : 0;

  return (
    <div className={styles.trimControls}>
      <ClickableDiv className={styles.playButton} onClick={handlePlayPause}>
        <Icon icon={isPlaying ? 'icon-pause' : 'icon-play-video'} size={IconSize.SIZE_ICON_16} type={IconType.NONE} shape={IconShape.NONE} />
      </ClickableDiv>
      <span className={styles.timeDisplay}>{`${formatTime(currentTime)} / ${formatTime(duration)}`}</span>
      <div ref={stripContainerRef} className={styles.stripContainer} onMouseDown={isStripReady ? handleStripMouseDown : undefined}>
        {!isStripReady && (
          <div className={styles.stripSkeleton}>
            <SkeletonBrick theme={SkeletonBrickTheme.DARK} isPulsating width="100%" height="100%" />
          </div>
        )}
        <div className={styles.stripInner}>
          <AnimationTrimThumbnailStrip
            source={source}
            duration={duration}
            videoWidth={videoWidth}
            videoHeight={videoHeight}
            preRenderedCanvas={preRenderedCanvas}
            onReady={(): void => {
              setIsStripReady(true);
            }}
          />
          {duration > 0 ? <div className={`${styles.trimOverlay} ${styles.trimOverlayLeft}`} style={{width: `${(localStartTime / duration) * 100}%`}} /> : null}
          {duration > 0 ? <div className={`${styles.trimOverlay} ${styles.trimOverlayRight}`} style={{width: `${((duration - localEndTime) / duration) * 100}%`}} /> : null}
          <span className={styles.durationBadge} style={{right: `calc(${badgeRightPercent}% + 15px)`}}>{`${selectedDuration}s`}</span>
        </div>
        <AnimationTrimRangeSlider
          min={0}
          max={duration}
          value={[localStartTime, localEndTime]}
          minGap={MIN_TRIM_GAP}
          onChange={handleRangeChange}
          onChangeComplete={handleRangeChange}
        />
        <div ref={playheadRef} className={styles.playhead} />
      </div>
      <ClickableDiv
        className={styles.confirmButton}
        onClick={(): void => {
          onConfirm(localStartTime, localEndTime);
        }}
      >
        <Icon icon={'icon-check'} size={IconSize.SIZE_ICON_16} type={IconType.NONE} shape={IconShape.NONE} />
      </ClickableDiv>
    </div>
  );
}
